/* eslint-disable camelcase */
import type { ProductCardClickTracking } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/interfaces/clickTracking';
import type { Product } from '@/domains/productDiscovery/algolia/interfaces/product';
import { productHasDiscount } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';

interface ComputeDefaultTrackingData {
  listName?: string;
  position: number;
  product: Pick<
    Product,
    | 'title'
    | 'categoriesLast'
    | 'articleId'
    | 'productId'
    | 'articleId'
    | 'defaultTitle'
    | 'categoriesLast'
    | 'categoriesLastId'
    | 'categoriesL0'
    | 'categoriesL0Id'
    | 'categoriesL1'
    | 'categoriesL1Id'
    | 'categoriesL2'
    | 'categoriesL2Id'
    | 'categoriesL3'
    | 'categoriesL3Id'
    | 'sellerName'
    | 'sellerId'
    | 'brandName'
    | 'brandId'
    | 'title'
    | 'modelId'
    | 'rating'
    | 'ratingCount'
    | 'isMmf'
    | 'banner'
    | 'categoriesLastId'
    | 'banner'
    | 'categoriesLastId'
    | 'hasProConditions'
    | 'isEcoResponsible'
    | 'isLocal'
    | 'hasPromoLabel'
    | 'isCompetitive'
    | 'commercialAnimation'
    | 'commercialOperationIds'
    | 'coupon'
    | 'isBestQualityOffer'
    | 'cpc'
    | 'prices'
  >;
  price: string | number;
  isSponsored?: boolean;
  deliveryMode?: string;
}

export const computeDefaultTrackingData = ({
  listName,
  position,
  product,
  price,
  isSponsored = false,
  deliveryMode,
}: ComputeDefaultTrackingData): ProductCardClickTracking => {
  const hasDiscount = productHasDiscount(
    product.prices.main,
    product.prices.retail,
  );
  return {
    event: 'interaction_product click',
    e_category: 'ecommerce',
    e_action: 'product click',
    e_label: product.title,
    amp_event_name: 'Click on product card',
    ...(product.cpc && { mgr: product.cpc }),
    pr_products: [
      {
        /**
         * Set the lowest category name as fallback scenario if none is given
         */
        list_name: listName || product.categoriesLast?.[0],

        pr_id: product.articleId,
        pr_product_id: product.productId,
        pr_article_id: product.articleId,
        pr_name: product.defaultTitle,

        pr_stock: 'available',
        pr_position: position + 1,
        pr_price: price,
        pr_base_price: price,

        pr_lowest_category_name: product.categoriesLast?.[0],
        pr_lowest_category_id: product.categoriesLastId,

        pr_family_name: product.categoriesL0?.[0],
        pr_family_id: product.categoriesL0Id?.[0],
        pr_category_name: product.categoriesL1?.[0],
        pr_category_id: product.categoriesL1Id?.[0],
        pr_subcategory_name: product.categoriesL2?.[0],
        pr_subcategory_id: product.categoriesL2Id?.[0],
        pr_subsubcategory_name: product.categoriesL3?.[0],
        pr_subsubcategory_id: product.categoriesL3Id?.[0],

        pr_seller_name: product.sellerName,
        pr_seller_id: product.sellerId,

        pr_brand: product.brandName,
        pr_brand_id: product.brandId,

        pr_model_name: product.title,
        pr_model_id: product.modelId,

        pr_variant: null,
        pr_attributes_count: null,

        pr_rating: product.rating,
        pr_rating_count: product.ratingCount,

        pr_is_restocking: false,
        pr_is_sponsored: isSponsored,
        pr_delivery_mode: deliveryMode,
        pr_is_mf: product.isMmf,
        pr_is_top_sales:
          product.banner?.categories?.includes(
            `topSales.${product.categoriesLastId}`,
          ) || false,
        pr_is_best_seller:
          product.banner?.categories?.includes(
            `topSales.${product.categoriesLastId}`,
          ) || false,
        pr_is_pro: product.hasProConditions || false,
        pr_is_sustainable_product: product.isEcoResponsible,
        pr_is_local_product: product.isLocal,
        pr_is_best_quality_offer:
          (product.isBestQualityOffer &&
            !product.banner?.categories?.includes(
              `topSales.${product.categoriesLastId}`,
            )) ||
          false,

        pr_has_promo_label:
          !!(
            product.commercialAnimation &&
            product.commercialAnimation.tags.length > 0
          ) &&
          hasDiscount &&
          !!(
            product.commercialAnimation.retailPriceTimeLeftLabel &&
            product.commercialAnimation.retailPriceTimeLeftLabel !== ''
          ),
        pr_has_coupon_label: !!product.coupon,
        pr_is_cpt: product.isCompetitive,
        pr_is_in_promo:
          !!(
            product.commercialAnimation &&
            product.commercialAnimation.tags.length > 0
          ) && hasDiscount,
      },
    ],
  };
};
